/**
 * ActiveLoansPanel — Top 5 active loans on the borrower Home page.
 *
 * Per the design pass spec (Screen 1 → Active loans panel):
 *   - Each row: MonoID short + Chip type + Chip stage tone + stage progress bar
 *     + "Next: {who} · {due}" hint.
 *   - Section header right slot: mint chip with a dot icon labelled
 *     "Auto-ranked by Astor".
 *   - "All loans" link → /applications.
 *
 * We read from the existing `useConsumerCases` tRPC hook and project the case
 * record into the row shape the spec expects. Fields that aren't yet present
 * on the backend (Astor priority ranking, next-action owner) are stubbed with
 * sensible defaults — see TODOs below.
 */

import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CircleIcon from '@mui/icons-material/Circle';
import {
  Box,
  baseColors,
  GlassButton,
  GlassChip,
  iconSizes,
  LinearProgress,
  ShimmerLoader,
  Typography,
  useI18n,
} from '@ops-matrix/design-system-ui';
import {
  type AstorNextAction,
  type AstorPriority,
  CONSUMER_STEPPER_STAGES,
  ConsumerLoanStage,
  getConsumerLoanStageLabelKey,
  type LoanStage,
  type LoanSubstage,
  toConsumerLoanStage,
} from '@ops-matrix/shared-types';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useConsumerCases } from '../../lib/trpc/hooks';
import { loanTypeColor } from '../loans/LoanRowCard';

/** Loose case row shape from `consumerCases.list`. The tRPC payload is
 *  intentionally untyped at the field level — we narrow inline so the
 *  surface stays free of `any` while leaving room for fields we don't read. */
interface CaseRow {
  id: string;
  caseNumber?: string | null;
  type?: string | null;
  loanStage?: LoanStage | null;
  loanSubstage?: LoanSubstage | null;
  // GH-03: backend doesn't yet emit `propertyAddress` on the list response —
  // when the case-mgmt service adds the field we'll prefer it over caseNumber
  // for the primary label so the row reads like a real estate listing.
  propertyAddress?: string | null;
  amount?: number | null;
  interestRate?: number | null;
  ltv?: number | null;
  // Astor priority + next-action are deterministic projections from
  // consumer.cases.list (see 2-api-gateway `computeAstorPriority` /
  // `computeNextAction`). Wire shape lives in `@ops-matrix/shared-types`.
  astorPriority?: AstorPriority | null;
  nextAction?: (AstorNextAction & { assignee?: string; due?: string; dueDate?: string }) | null;
}

const PIPELINE_STAGES = CONSUMER_STEPPER_STAGES.filter((s) => s !== ConsumerLoanStage.TERMINAL);

const PRIORITY_ORDER: Record<AstorPriority, number> = {
  urgent: 0,
  high: 1,
  normal: 2,
  low: 3,
};

function stageProgress(stage: ConsumerLoanStage): number {
  if (stage === ConsumerLoanStage.TERMINAL) return 100;
  const idx = PIPELINE_STAGES.indexOf(stage);
  if (idx < 0) return 0;
  return Math.round(((idx + 1) / PIPELINE_STAGES.length) * 100);
}

/** Map the consumer-facing stage onto a tone-coloured GlassChip per the design pass. */
function stageChipColor(stage: ConsumerLoanStage): 'warning' | 'success' | 'info' | 'default' {
  switch (stage) {
    case ConsumerLoanStage.APPLIED:
      return 'warning'; // awaiting-you → amber
    case ConsumerLoanStage.REVIEWING:
    case ConsumerLoanStage.OFFER:
      return 'info'; // with-lender → id (blue)
    case ConsumerLoanStage.ACCEPTED:
    case ConsumerLoanStage.CLOSING:
    case ConsumerLoanStage.FUNDED:
    case ConsumerLoanStage.ACTIVE:
      return 'success'; // active → mint
    default:
      return 'default';
  }
}

/** Compute days-until between now and an ISO date string. Returns null if unparseable. */
function daysUntil(dateStr: string | null | undefined): number | null {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return null;
  return Math.ceil((d.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
}

/** Tone the "Due" chip by urgency window. */
function dueChipColor(days: number | null): 'error' | 'warning' | 'success' | 'default' {
  if (days === null) return 'default';
  if (days < 3) return 'error';
  if (days < 7) return 'warning';
  return 'success';
}

/** Map the Astor priority bit onto a left-border accent color (theme token). */
function priorityBorderColor(priority: AstorPriority | null | undefined): string {
  switch (priority) {
    case 'urgent':
      return 'error.main';
    case 'high':
      return 'warning.main';
    case 'normal':
      return 'primary.main';
    case 'low':
    default:
      return 'divider';
  }
}

/** Format a USD amount as `$1.5M` / `$420K` / `$0`. */
function formatCompactUsd(value: number): string {
  return new Intl.NumberFormat('en-US', {
    notation: 'compact',
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 1,
  }).format(value);
}

export function ActiveLoansPanel(): React.ReactElement {
  const { t } = useI18n();
  const navigate = useNavigate();

  // Pull a few more than 5 so we can stably show the top 5. The tRPC route
  // now emits `astorPriority` + `nextAction` per case (deterministic
  // projection — see api-gateway `computeAstorPriority` / `computeNextAction`).
  // Future Wave-3 LLM swap keeps this hook contract stable.
  const { data, isLoading } = useConsumerCases({ page: 1, limit: 10 });

  // Sort amber-first (urgent → high → normal → low) so the most-urgent cases
  // land at the top of the 5-row cap. The tRPC route already emits
  // astorPriority + nextAction per row; no projection layer needed.
  const rows = useMemo<CaseRow[]>(() => {
    const raw = (data?.data ?? []) as CaseRow[];
    return [...raw]
      .sort((a, b) => PRIORITY_ORDER[a.astorPriority ?? 'normal'] - PRIORITY_ORDER[b.astorPriority ?? 'normal'])
      .slice(0, 5);
  }, [data]);

  const handleOpenCase = (id: string) => navigate(`/applications/${id}`);

  return (
    <Box
      sx={{
        p: 3,
        borderRadius: 2,
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h6">{t('consumer.home.loans.title')}</Typography>
          <GlassChip
            size="small"
            color="success"
            icon={<CircleIcon sx={{ fontSize: iconSizes['2xs'], color: baseColors.teal[400] }} />}
            label={t('consumer.home.loans.astorRanked')}
            sx={{ height: 24 }}
          />
        </Box>
        <GlassButton size="small" variant="text" onClick={() => navigate('/applications')}>
          {t('consumer.home.loans.viewAll')}
        </GlassButton>
      </Box>

      {isLoading ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {[1, 2, 3, 4, 5].map((i) => (
            <ShimmerLoader key={i} variant="rectangular" height={64} />
          ))}
        </Box>
      ) : rows.length === 0 ? (
        <Typography color="textSecondary" sx={{ py: 4, textAlign: 'center' }}>
          {t('consumer.home.loans.empty')}
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {rows.map((row) => {
            const consumerStage = row.loanStage
              ? toConsumerLoanStage(row.loanStage, row.loanSubstage ?? undefined)
              : ConsumerLoanStage.APPLIED;
            const progress = stageProgress(consumerStage);
            const stageLabel = t(getConsumerLoanStageLabelKey(consumerStage));
            // GH-03: prefer `propertyAddress` once the backend emits it.
            // Falls back to `caseNumber`, then a slice of the case id so
            // the row is never empty.
            const primaryLabel = row.propertyAddress ?? row.caseNumber ?? row.id.slice(0, 8);
            const monoId = row.caseNumber ?? row.id.slice(0, 8);
            const loanType = row.type ?? t('consumer.home.loans.defaultType');
            const typeChipColor = loanTypeColor(row.type);

            // Compose "$X.XM · X.XX% · XX% LTV" only when ALL fields are
            // present — otherwise render nothing rather than half a value.
            const hasMoneyMeta =
              typeof row.amount === 'number' && typeof row.interestRate === 'number' && typeof row.ltv === 'number';
            const moneyMeta = hasMoneyMeta
              ? `${formatCompactUsd(row.amount as number)} · ${(row.interestRate as number).toFixed(2)}% · ${Math.round((row.ltv as number) * 100)}% LTV`
              : null;

            // H4: priority bit drives left-border tone.
            const borderToneColor = priorityBorderColor(row.astorPriority ?? 'normal');

            // Next action — pull richer fields off the new optional shape if
            // present; fall back to the legacy who/due defaults.
            const nextAssignee =
              row.nextAction?.assignee ?? row.nextAction?.who ?? t('consumer.home.loans.nextWhoDefault');
            const nextLabel = row.nextAction?.label ?? t('consumer.home.loans.nextDueDefault');
            const nextDueDateDays = daysUntil(row.nextAction?.dueDate);
            const dueChipTone = dueChipColor(nextDueDateDays);
            const dueChipLabel =
              nextDueDateDays === null
                ? t('consumer.home.loans.nextDueDefault')
                : nextDueDateDays <= 0
                  ? t('consumer.home.loanRow.dueToday')
                  : t('consumer.home.loanRow.dueInDays', { days: nextDueDateDays });

            return (
              <Box
                key={row.id}
                onClick={() => handleOpenCase(row.id)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') handleOpenCase(row.id);
                }}
                sx={{
                  p: 2,
                  borderRadius: 1.5,
                  border: '1px solid',
                  borderColor: 'divider',
                  // H5: 3px left border tone-mapped to Astor priority bit.
                  // Declared AFTER the `border` shorthand so MUI applies it
                  // on top of the 1px divider.
                  borderLeftWidth: '3px',
                  borderLeftStyle: 'solid',
                  borderLeftColor: borderToneColor,
                  cursor: 'pointer',
                  minHeight: 44,
                  transition: 'border-color 120ms ease, background-color 120ms ease',
                  '&:hover, &:focus-visible': {
                    borderColor: 'primary.main',
                    borderLeftColor: borderToneColor,
                    bgcolor: 'action.hover',
                    outline: 'none',
                  },
                }}
              >
                {/* H2: 2-column CSS grid (left = info column, right = next-action column). */}
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: 'minmax(0, 1fr) 200px' },
                    columnGap: 2,
                    rowGap: 1,
                    alignItems: 'start',
                  }}
                >
                  {/* LEFT COLUMN — primary label, chips, money meta, progress, stage chip */}
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        color: 'text.primary',
                        // 1-line clamp keeps long property addresses tidy.
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {primaryLabel}
                    </Typography>
                    <Box
                      sx={{
                        mt: 0.75,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        flexWrap: 'wrap',
                      }}
                    >
                      {/* H4: type chip color reuses loanTypeColor() — orange for
                          bridge/hardmoney, secondary for DSCR/rental, info for
                          construction, success for commercial, default otherwise. */}
                      <GlassChip label={loanType} size="small" color={typeChipColor} sx={{ height: 22 }} />
                      <Typography
                        variant="caption"
                        sx={{
                          fontFamily: 'monospace',
                          color: 'text.secondary',
                          letterSpacing: '0.05em',
                        }}
                      >
                        {monoId}
                      </Typography>
                    </Box>
                    {moneyMeta && (
                      <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5 }}>
                        {moneyMeta}
                      </Typography>
                    )}
                    {/* H8: stage progress bar height 4 (was 6). */}
                    <LinearProgress
                      variant="determinate"
                      value={progress}
                      sx={{ mt: 1.25, height: 4, borderRadius: 2 }}
                    />
                    <Box sx={{ mt: 1 }}>
                      <GlassChip
                        label={stageLabel}
                        size="small"
                        color={stageChipColor(consumerStage)}
                        sx={{ height: 22 }}
                      />
                    </Box>
                  </Box>

                  {/* RIGHT COLUMN — "Next · {who}" overline + 2-line label + due chip + chevron */}
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 0.5,
                      minWidth: 0,
                      alignSelf: 'stretch',
                      position: 'relative',
                    }}
                  >
                    <Typography
                      variant="overline"
                      sx={{
                        color: 'text.secondary',
                        letterSpacing: '0.08em',
                        display: 'block',
                      }}
                    >
                      {t('consumer.home.loanRow.nextAssignee', { who: nextAssignee })}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'text.primary',
                        // 2-line clamp for the action label.
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                    >
                      {nextLabel}
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.75,
                        mt: 'auto',
                      }}
                    >
                      <GlassChip label={dueChipLabel} size="small" color={dueChipTone} sx={{ height: 20 }} />
                      <Box sx={{ flexGrow: 1 }} />
                      <ChevronRightIcon aria-hidden sx={{ fontSize: iconSizes.sm, color: 'text.disabled' }} />
                    </Box>
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
}
